import { ZowieControl } from "components";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import { PressLogos } from "modules/common/components/PressLogos";
import { renderDynamicComponent } from "modules/storyblok/DynamicComponent";
import StickyBottomBarAdapter from "modules/storyblok/adapters/StickyBottomBar";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { GlobalComponentsWrapper } from "modules/storyblok/globalComponents/GlobalComponentsWrapper";
import type { FlexibleTemplateProps } from "./FlexibleTemplate.d";

const FlexibleTemplate = ({
  story,
  cmsLanguagesData,
  whiteListDomains,
  serverSideStoryblokPages,
  storyblokComponentData: { trustpilotInfo, reviews },
  nofollowUrls,
  globalComponents,
}: FlexibleTemplateProps) => {
  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      body,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      ogTitle,
      ogImageUrl,
      ogDescription,
      stickyBottomBarHeading,
      stickyBottomBarButtonText,
      stickyBottomBarBenefits,
      stickyBottomBarHideBenefits,
      hideStickyBottomBar,
      stickyBottomBarVariant,
      activeButtonOption = "view-claim-status",
    },
  } = story;
  const noindex = tagList.includes("noindex");

  return (
    <DataContextProvider
      data={{
        reviews,
        cmsLanguagesData,
        serverSideStoryblokPages,
        trustpilotInfo,
        whiteListDomains,
        nofollowUrls,
      }}
    >
      <CustomHead
        title={metaTitle}
        description={metaDescription}
        noindex={noindex}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImageUrl={ogImageUrl}
      />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <ModalContextProvider>
        <GlobalComponentsWrapper
          globalComponents={globalComponents}
          globalComponentsProps={{ header: { headerActionButton: activeButtonOption } }}
        >
          <ZowieControl />
          <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
          {renderDynamicComponent(body)}
          <PressLogos />
        </GlobalComponentsWrapper>
      </ModalContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
        stickyBottomBarButtonText={stickyBottomBarButtonText}
        stickyBottomBarBenefits={stickyBottomBarBenefits}
        stickyBottomBarHideBenefits={stickyBottomBarHideBenefits}
      />
    </DataContextProvider>
  );
};

export const FlexibleTemplateAdapter = (props: FlexibleTemplateProps) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <FlexibleTemplate {...props} />
  </StickyBottomBarContextProvider>
);
